'use client'

import { useEffect, useState } from 'react'

/**
 * Apply debouncing to the given value (value) changes.
 *
 * @template T The type of the value to debounce
 * @param {T} value The value to debounce (usually the input state value)
 * @param {number} delay The debouncing delay time (in milliseconds)
 * @returns {{ value: T }} The debounced value updated after the delay time
 */
export function useDebounce<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  // No `isDebouncing` flag — every current caller only reads `value`, and
  // tracking that flag meant an extra state update (and re-render of
  // whatever heavy component called this) on every single keystroke, on top
  // of the one the value change itself already causes.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timeout)
  }, [value, delay])

  return { value: debouncedValue }
}
